import { inject, observer } from "mobx-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import ClientManagementStore from "../../../modules/Admin/ClientManagement/store";
import {
  ClientCategoryDto,
  ClientDto,
} from "../../../modules/Admin/ClientManagement/types";
import { MessageResultState } from "../../../types/general-types";
import { findCategory, getCategoryName } from "../../config/categories";
import { REDESIGN_NS } from "../../i18n";
import {
  fetchClientCategories,
  invalidateClientCategories,
} from "../../lib/clientCategories";
import { notifyPortalDataChanged } from "../../lib/portalData";
import { getPortalDescription, getPortalName } from "../../lib/openPortal";
import { IconPencil, IconPlus, IconTrash } from "../../ui/icons";
import { ConfirmModal } from "../../ui/Modal";
import PortalFormModal from "./PortalFormModal";
import "./client-management.css";

interface Props {
  ClientManagementStore?: ClientManagementStore;
}

/** Liste `get-all` üzerinden gelir: local/geliştirme portallarını da içerir. */
const ClientManagementView: FC<Props> = inject("ClientManagementStore")(
  observer(({ ClientManagementStore: store }: Props) => {
    const { t, i18n } = useTranslation(REDESIGN_NS);

    const [clients, setClients] = useState<ClientDto[]>([]);
    const [categories, setCategories] = useState<ClientCategoryDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selected, setSelected] = useState<ClientDto>();
    const [pendingDelete, setPendingDelete] = useState<ClientDto>();
    const [isDeleting, setIsDeleting] = useState(false);

    const load = useCallback(() => {
      setIsLoading(true);
      store
        ?.getAll()
        .then((response) => {
          if (response.state === MessageResultState.SUCCESS) {
            setClients(response.result || []);
          }
        })
        // Hata toast'ını interceptor basıyor; burası yalnızca spinner'ı kapatır.
        .catch(() => undefined)
        .then(() => setIsLoading(false));
    }, [store]);

    useEffect(load, [load]);

    const loadCategories = useCallback(() => {
      fetchClientCategories().then(setCategories);
    }, []);

    useEffect(loadCategories, [loadCategories]);

    // `invalidate` uçuştaki isteği düşürür ve aboneleri uyarır; mega menü de tazelenir.
    const reloadCategories = useCallback(() => {
      invalidateClientCategories();
      fetchClientCategories().then(setCategories);
    }, []);

    const openCreate = () => {
      setSelected(undefined);
      setIsFormOpen(true);
    };

    const openEdit = (client: ClientDto) => {
      setSelected(client);
      setIsFormOpen(true);
    };

    const confirmDelete = async () => {
      if (!pendingDelete || !store) return;
      setIsDeleting(true);
      try {
        await store.delete(pendingDelete.id);
        setPendingDelete(undefined);
        load();
        notifyPortalDataChanged();
      } finally {
        setIsDeleting(false);
      }
    };

    const language = i18n.language;

    return (
      <div className="sso-rd__container">
        <div className="sso-rd__page-head">
          <div className="sso-rd__page-head-text">
            <div className="sso-rd__eyebrow">{t("Company Name")}</div>
            <h1 className="sso-rd__page-title">{t("Portal management")}</h1>
          </div>

          <button
            type="button"
            className="sso-rd__btn sso-rd__btn--primary"
            onClick={openCreate}
          >
            <IconPlus size={17} />
            {t("Add New Portal")}
          </button>
        </div>

        {isLoading && <Loading />}

        <div className="sso-rd__client-grid">
          {clients.map((client) => {
            const def = findCategory(categories, client.clientCategoryId);
            return (
              <div className="sso-rd__client-card" key={client.id}>
                <div className="sso-rd__client-top">
                  <h3 className="sso-rd__client-title">
                    {getPortalName(client, language)}
                  </h3>
                  <div className="sso-rd__client-actions">
                    <button
                      type="button"
                      className="sso-rd__icon-btn sso-rd__icon-btn--sm sso-rd__icon-btn--neutral"
                      onClick={() => openEdit(client)}
                      title={t("Edit")}
                      aria-label={t("Edit")}
                    >
                      <IconPencil size={15} />
                    </button>
                    <button
                      type="button"
                      className="sso-rd__icon-btn sso-rd__icon-btn--sm"
                      onClick={() => setPendingDelete(client)}
                      title={t("Delete")}
                      aria-label={t("Delete")}
                    >
                      <IconTrash size={15} />
                    </button>
                  </div>
                </div>

                <p className="sso-rd__client-desc">
                  {getPortalDescription(client, language)}
                </p>

                <div className="sso-rd__client-foot">
                  <span className="sso-rd__chip">
                    {getCategoryName(def, language)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {isFormOpen && (
          <PortalFormModal
            isOpen={isFormOpen}
            onClose={() => setIsFormOpen(false)}
            selected={selected}
            categories={categories}
            onCategoriesChanged={(portalsMoved) => {
              reloadCategories();
              // Kategori eklemek portalleri değiştirmiyor; liste boşuna yeniden
              // yüklenirse açık portal formunun arkasında spinner belirir.
              if (portalsMoved) load();
            }}
            onSaved={() => {
              setIsFormOpen(false);
              load();
              // Portalin kategorisi degismis olabilir; kabuktaki mega menu de tazelenir.
              notifyPortalDataChanged();
            }}
          />
        )}

        <ConfirmModal
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(undefined)}
          onConfirm={confirmDelete}
          isBusy={isDeleting}
        />
      </div>
    );
  })
) as FC<Props>;

export default ClientManagementView;
